import { FUSIONNOW_BASE_URL, getFusionNowToken } from './fusionNowAuth';

const MARKETPLACE_SECRET =
  process.env.REACT_APP_FUSIONNOW_APP_SECRET_KEY ?? '';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RecipeApp {
  order: number;
  app_type:
    | 'trigger'
    | 'action'
    | 'if_condition'
    | 'wait'
    | 'exit_condition'
    | string;
  app?: {
    name: string;
    icon: { url: string };
  };
}

export interface Recipe {
  id: number;
  name: string;
  status: 'active' | 'draft' | 'stopped' | string;
  key: string;
  user_id: number;
  account_id: number;
  created_at: string;
  updated_at: string;
  preview: boolean;
  description: string | null;
  folder_id: number | null;
  is_basic: boolean;
  is_public: boolean;
  folder_name: string | null;
  is_valid: boolean;
  recipe_apps?: RecipeApp[];
}

export interface RecipesMeta {
  current_page: number;
  next_page: number | null;
  prev_page: number | null;
  total_pages: number;
  total_count: number;
}

export interface RecipesResponse {
  recipes: Recipe[];
  meta: RecipesMeta;
}

// ── API calls ─────────────────────────────────────────────────────────────────

export async function getRecipes(
  page = 1,
  search = '',
  perPage = 12
): Promise<RecipesResponse> {
  const searchParam = search.trim()
    ? `&keyword=${encodeURIComponent(search.trim())}`
    : '';
  const url = `${FUSIONNOW_BASE_URL}/api/marketplace/community_pages?per_page=${perPage}&page=${page}${searchParam}`;

  const res = await fetch(url, {
    headers: { 'X-Marketplace-Secret': MARKETPLACE_SECRET }
  });
  if (!res.ok) throw new Error(`Failed to load recipes: ${res.status}`);

  const data = await res.json();
  if (!data.success) throw new Error('Recipes API returned success: false');

  return {
    recipes: data.data as Recipe[],
    meta: data.meta_attributes as RecipesMeta
  };
}

export async function getMyRecipes(
  page = 1,
  search = '',
  perPage = 12
): Promise<RecipesResponse> {
  const token = await getFusionNowToken();
  const searchParam = search.trim()
    ? `&keyword=${encodeURIComponent(search.trim())}`
    : '';
  const url = `${FUSIONNOW_BASE_URL}/api/open_api/v1/recipes?per_page=${perPage}&page=${page}${searchParam}`;

  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) throw new Error(`Failed to load my recipes: ${res.status}`);

  const data = await res.json();
  if (!data.success) throw new Error('My Recipes API returned success: false');

  return {
    recipes: data.data as Recipe[],
    meta: data.meta_attributes as RecipesMeta
  };
}

export async function cloneRecipe(key: string): Promise<{ success: boolean }> {
  const token = await getFusionNowToken();

  const res = await fetch(
    `${FUSIONNOW_BASE_URL}/api/open_api/v1/community_pages/clone_recipe`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ key })
    }
  );
  if (!res.ok) throw new Error(`Clone failed: ${res.status}`);

  const data = await res.json();
  return { success: data.success ?? true };
}
